import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { Api } from 'src/smoothr-web-app-core/api/api';
import GiftCard from 'src/smoothr-web-app-core/models/GiftCard';
import Order from 'src/smoothr-web-app-core/models/Order';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { AnalyticsService } from 'src/smoothr-web-app-core/services/analytics/analytics.service';
import { RepositoryService } from 'src/smoothr-web-app-core/services/repository/repository.service';
import { OrderUtils } from 'src/smoothr-web-app-core/utils/order-utils';
import { axiosErrorToMessage, numberToCurrency, sleep } from 'src/smoothr-web-app-core/utils/utils';
enum TypeViewCode {
	GiftCard = 'gift_card',
	Code = 'code',
}
@Component({
	selector: 'app-add-gift-card-dialog',
	templateUrl: './add-gift-card-dialog.component.html',
	styleUrls: ['./add-gift-card-dialog.component.scss'],
})
export class AddGiftCardDialogComponent implements OnInit {
	public enterGiftCard = new FormControl('');
	public enterPromoCode = new FormControl('');
	public promoError: string = '';
	public loading: boolean = false;
	public venue: Venue;
	public order: Order;
	pageShowType = TypeViewCode;
	changerPageValue = TypeViewCode.GiftCard;
	constructor(
		private modalCtrl: ModalController,
		public translate: TranslateService,
		private repository: RepositoryService,
		private analytics: AnalyticsService
	) {}
	static async show(modalCtrl: ModalController, venue: Venue, order: Order): Promise<{ giftCard: GiftCard; order: Order }> {
		const modal = await modalCtrl.create({
			component: AddGiftCardDialogComponent,
			cssClass: 'auto-height',
			componentProps: {
				venue: venue,
				order: order,
			},
			showBackdrop: true,
			backdropDismiss: true,
		});
		await modal.present();

		const result = (await modal.onDidDismiss()).data;
		return result;
	}

	ngOnInit() {
		combineLatest(this.enterPromoCode.valueChanges, this.enterGiftCard.valueChanges)
			.pipe()
			.subscribe(v => {
				this.promoError = '';
			});
	}
	dismiss() {
		this.modalCtrl.dismiss();
	}
	async checkGiftCard() {
		if (!this.enterGiftCard.value) {
			this.promoError = this.translate.instant('add_gift_card.error');
			return;
		}
		this.loading = true;
		this.promoError = null;
		try {
			const giftCard = (await Api.loadGiftCard(this.enterGiftCard.value.trim(), this.venue._id)).data;
			if (!giftCard.active) {
				this.promoError = this.translate.instant('add_gift_card.error');
				return;
			}
			try {
				this.loading = false;
				giftCard.hash = this.enterGiftCard.value.trim();
				this.modalCtrl.dismiss({ giftCard });
			} catch (e) {
				this.promoError = e;
				this.loading = false;
			}
		} catch (e) {
			this.loading = false;
			if (e.response.status === 404) {
				this.promoError = this.translate.instant('add_gift_card.error');
				return;
			}
			this.promoError = axiosErrorToMessage(this.translate, e);
			return;
		}
	}
	changePage(value: TypeViewCode) {
		this.changerPageValue = value;
	}
	async checkPromoCode() {
		if (!this.enterPromoCode.value) {
			this.promoError = this.translate.instant('promo_code.not_valid');
			return;
		}
		this.loading = true;
		this.promoError = null;
		try {
			const promoCode = (
				await Api.getPromoCode({
					code: this.enterPromoCode.value.trim(),
					venue: this.order.venue,
				})
			).data;
			promoCode.mov = +promoCode.mov;
			if (OrderUtils.orderTotalPrice(this.order, false, false) < promoCode.mov) {
				this.promoError = this.translate.instant('promo_code.mov_not_reached', {
					mov: numberToCurrency(promoCode.mov, this.order.currency),
				});
				this.loading = false;

				return;
			}
			try {
				try {
					const orderCopy = JSON.parse(JSON.stringify(this.order));
					orderCopy.promoCode = promoCode;
					orderCopy.userUid = this.repository.customer.uid;
					orderCopy.orderAt = null;
					await Api.applyPromoCode(orderCopy);
				} catch (e) {
					this.loading = false;

					this.promoError = axiosErrorToMessage(this.translate, e);
					return;
				}
				this.modalCtrl.dismiss({
					order: OrderUtils.applyPromo(this.translate, this.repository._venue, this.order, promoCode, this.analytics),
				});
				this.loading = false;
			} catch (e) {
				this.promoError = e;
				this.loading = false;
			}
		} catch (e) {
			this.loading = false;
			if (e.response.status === 404) {
				this.promoError = this.translate.instant('promo_code.not_valid');
				return;
			}
			this.promoError = axiosErrorToMessage(this.translate, e);
			return;
		}
	}
}
